import { ResponsivePie } from '@nivo/pie';
import React, { useMemo } from 'react';
import { Loader } from 'semantic-ui-react';

interface PieChartProps<T> {
  data: T[];
  formatValue?: (value: number) => string;
  showPercentage?: boolean;
  colorScheme?: string[];
}

const DefaultPieChart = <T,>({
  data,
  formatValue,
  showPercentage = false,
  colorScheme,
}: PieChartProps<T>) => {
  const totalValue = useMemo(() => {
    if (!showPercentage) return 0;
    return data.reduce((sum, item: any) => sum + (Number(item.value) || 0), 0);
  }, [data, showPercentage]);

  // Create value formatter based on showPercentage
  const valueFormatter = useMemo(() => {
    if (formatValue) return formatValue;

    if (showPercentage && totalValue > 0) {
      return (value: number) => {
        const percentage = Math.round((value / totalValue) * 100);
        return `${percentage}%`;
      };
    }

    // Otherwise use the original formatter
    return (value: number) => `${value.toLocaleString('fr-FR')} €`;
  }, [formatValue, showPercentage, totalValue]);

  return (
    <div style={{ height: 400, textAlign: 'center' }}>
      {data.length > 0 ? (
        <ResponsivePie
          data={data}
          margin={{ top: 40, right: 150, bottom: 40, left: 40 }}
          innerRadius={0.5}
          colors={colorScheme ?? { scheme: 'nivo' }}
          padAngle={0.7}
          cornerRadius={3}
          borderWidth={1}
          borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
          valueFormat={valueFormatter}
          arcLabelsSkipAngle={15}
          enableArcLinkLabels={false}
          activeOuterRadiusOffset={5}
          legends={[
            {
              anchor: 'right',
              direction: 'column',
              justify: false,
              translateX: 90,
              translateY: 0,
              itemsSpacing: 1,
              itemWidth: 100,
              itemHeight: 18,
              itemTextColor: '#999',
              itemDirection: 'left-to-right',
              itemOpacity: 1,
              symbolSize: 18,
              symbolShape: 'circle',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemTextColor: '#000',
                  },
                },
              ],
            },
          ]}
        />
      ) : (
        <div style={{ paddingTop: '175px' }}>
          <Loader size="large" active inline />
        </div>
      )}
    </div>
  );
};

export default DefaultPieChart;
